import { memo } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { PreviewAccountOrigin } from "@common/types/domains/accounting/chartOfAccountsTemplateApply";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ChartOfAccountsRow } from "@components/accounting/chartOfAccounts/utils";
import {
    ApplyCoaPreviewTableMeta,
    PreviewOriginEntity,
} from "./ApplyCoaPreviewNameCell";
import styles from "./ApplyCoaPreviewOriginCell.module.scss";

const EntityLabel = ({ entity }: { entity: PreviewOriginEntity }) => (
    <span className={styles.item}>
        <EntityAvatar size="xs" entity={entity} aria-hidden />
        <span className={styles.label}>{entity.name}</span>
    </span>
);

const TemplateLabel = () => <span className={styles.label}>Template</span>;

const OriginLabels = ({
    origin,
    entity,
}: {
    origin: PreviewAccountOrigin;
    entity: PreviewOriginEntity;
}) => {
    if (origin === PreviewAccountOrigin.templateOnly) {
        return <TemplateLabel />;
    }
    if (origin === PreviewAccountOrigin.matched) {
        return (
            <span className={styles.both}>
                <span className={styles.label}>Template,</span>
                <EntityLabel entity={entity} />
            </span>
        );
    }
    return <EntityLabel entity={entity} />;
};

// Shows where an account or group starts in the template <> entity diff:
// entity-only -> the company (avatar + name), template-only -> "Template",
// matched -> both, rendered as "Template, {avatar}{name}".
export const ApplyCoaPreviewOriginCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, ChartOfAccountsRow, unknown>) => {
        const rowData = row.original;
        const meta = table.options.meta as ApplyCoaPreviewTableMeta | undefined;
        const originEntity = meta?.originEntity;
        if (!meta || !originEntity) {
            return <ViewTableCell />;
        }

        let origin: PreviewAccountOrigin | undefined;
        if (rowData.account) {
            origin =
                meta.accountMetaById.get(rowData.account.id)?.origin ??
                PreviewAccountOrigin.entityOnly;
        } else if (rowData.group) {
            origin = meta.groupOriginById?.get(rowData.group.id);
        }
        if (origin === undefined) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell>
                <OriginLabels origin={origin} entity={originEntity} />
            </ViewTableCell>
        );
    },
);
ApplyCoaPreviewOriginCell.displayName = "ApplyCoaPreviewOriginCell";
